import { ref, computed, readonly } from 'vue';
import type { Ref } from 'vue';
import type { FlowStructureDto, FlowStepDto } from '@/types/flow-structure';
import type {
  FlowEditorMode,
  FlowSelectionState,
  BranchMergeData,
  BranchClosureData,
  BranchClosureOutput,
} from '@/types/flow-types';
import { getOperatorById } from '@/components/flows/operators/registry';
import { parseOperatorParameters } from '@/helpers/flowParameters';

/**
 * Composable for managing branch operations (merge and closure)
 * 
 * Usage:
 * ```
 * const branchOps = useBranchOperations(flow);
 * branchOps.startMerge(stepId, outputIndex);
 * // User selects target...
 * branchOps.completeMerge(targetStepId);
 * ```
 */
export function useBranchOperations(flow: Ref<FlowStructureDto | null>) {
  const selectionState = ref<FlowSelectionState>({
    mode: 'normal',
    availableTargets: [],
  });

  /**
   * Get all ancestors of a step (to prevent cycles in merge)
   */
  const getAncestors = (stepId: string): string[] => {
    if (!flow.value) return [];
    
    const ancestors: string[] = [];
    const visited = new Set<string>();
    
    const traverse = (id: string) => {
      if (visited.has(id)) return;
      visited.add(id);
      
      const step = flow.value?.steps.find(s => s.id === id);
      if (!step) return;
      
      step.parents.forEach(parent => {
        ancestors.push(parent.id);
        traverse(parent.id);
      });
    };
    
    traverse(stepId);
    return ancestors;
  };

  /**
   * Get all descendants of a step
   */
  const getDescendants = (stepId: string): string[] => {
    if (!flow.value) return [];
    
    const descendants: string[] = [];
    const visited = new Set<string>();
    
    const traverse = (id: string) => {
      if (visited.has(id)) return;
      visited.add(id);
      
      const step = flow.value?.steps.find(s => s.id === id);
      if (!step) return;
      
      step.children.forEach(child => {
        descendants.push(child.id);
        traverse(child.id);
      });
    };
    
    traverse(stepId);
    return descendants;
  };

  /**
   * Check if a step has a parent with multiple outputs
   */
  const hasParentWithMultipleOutputs = (stepId: string): boolean => {
    if (!flow.value) return false;
    
    const step = flow.value.steps.find(s => s.id === stepId);
    if (!step || !step.primaryParent) return false;
    
    const ancestors = getAncestors(stepId);
    return ancestors.some(ancestorId => {
      const ancestor = flow.value?.steps.find(s => s.id === ancestorId);
      if (!ancestor) return false;
      // Check if ancestor has multiple outputs by looking at connections
      const outputsUsed = new Set(
        flow.value?.connections
          .filter(c => c.parentId === ancestorId)
          .map(c => c.parentOutput) || []
      );
      return outputsUsed.size > 1 || ancestor.children.length > 1;
    });
  };

  /**
   * Get the total number of outputs for a step
   */
  const getStepOutputCount = (step: FlowStepDto): number => {
    const operator = getOperatorById(step.type);
    if (!operator) return 1;
    
    const outputsDef = operator.outputs ?? 1;
    if (typeof outputsDef === 'function') {
      return outputsDef(parseOperatorParameters(step.parameters));
    }
    return outputsDef;
  };

  /**
   * Check if a step is a multi-output node (excluding filters)
   */
  const isMultiOutputNonFilter = (step: FlowStepDto): boolean => {
    const operator = getOperatorById(step.type);
    if (!operator) return false;
    
    const totalOutputs = getStepOutputCount(step);
    const isFilter = operator.kind === 'filter';
    
    return totalOutputs > 1 && !isFilter;
  };

  /**
   * Get free outputs that can be used for closure
   * Returns outputs that are not connected and belong to steps that can be merged
   * Follows old system logic: exclude multi-output non-filter nodes
   */
  const getFreeOutputsForClosure = (sourceStepId: string, sourceOutput: number): string[] => {
    if (!flow.value) return [];
    
    const targets: string[] = [];
    
    flow.value.steps.forEach(step => {
      // Skip the source step itself
      if (step.id === sourceStepId) return;
      
      // Skip multi-output nodes that are not filters (old system behavior)
      if (isMultiOutputNonFilter(step)) return;
      
      // Get total outputs for this step
      const totalOutputs = getStepOutputCount(step);
      
      // Get connected outputs
      const connectedOutputs = flow.value?.connections
        .filter(c => c.parentId === step.id)
        .map(c => c.parentOutput) || [];
      
      // Find free outputs (not connected)
      for (let output = 1; output <= totalOutputs; output++) {
        if (!connectedOutputs.includes(output)) {
          targets.push(`${step.id}:${output}`);
        }
      }
    });
    
    return targets;
  };

  /**
   * Check if merge/closure options should be shown for a given step/output
   */
  const canShowBranchOptions = computed(() => {
    return (stepId: string, output: number): boolean => {
      if (!flow.value) return false;
      
      // Check if this output is free (not connected)
      const isOutputFree = !flow.value.connections.some(
        c => c.parentId === stepId && c.parentOutput === output
      );
      
      if (!isOutputFree) return false;
      
      // Check if there are other steps to merge with
      const step = flow.value.steps.find(s => s.id === stepId);
      if (!step) return false;
      
      // For merge: need at least one other step that's not an ancestor
      const ancestors = getAncestors(stepId);
      const hasValidMergeTargets = flow.value.steps.some(
        s => s.id !== stepId && !ancestors.includes(s.id)
      );
      
      // For closure: need at least one other free output
      const freeOutputs = getFreeOutputsForClosure(stepId, output);
      const hasValidClosureTargets = freeOutputs.length > 0;
      
      return hasValidMergeTargets || hasValidClosureTargets;
    };
  });

  /**
   * Start merge operation - select an input to connect to
   */
  const startMerge = (stepId: string, output: number): void => {
    if (!flow.value) return;
    
    // Calculate valid targets (exclude ancestors to prevent cycles)
    const ancestors = getAncestors(stepId);
    const descendants = getDescendants(stepId);
    
    const validTargets = flow.value.steps
      .filter(s => 
        s.id !== stepId && 
        !ancestors.includes(s.id) &&
        !descendants.includes(s.id)
      )
      .map(s => s.id);
    
    selectionState.value = {
      mode: 'merge-select',
      mergeData: {
        sourceStepId: stepId,
        sourceOutput: output,
      },
      availableTargets: validTargets,
    };
  };

  /**
   * Select target for merge operation
   */
  const selectMergeTarget = (targetStepId: string): void => {
    if (selectionState.value.mode !== 'merge-select' || !selectionState.value.mergeData) return;
    
    selectionState.value.mergeData.targetStepId = targetStepId;
  };

  /**
   * Get merge data for completion
   */
  const getMergeData = (): BranchMergeData | null => {
    if (selectionState.value.mode !== 'merge-select') return null;
    return selectionState.value.mergeData || null;
  };

  /**
   * Start closure operation - select outputs to merge
   */
  const startClosure = (stepId: string, output: number): void => {
    if (!flow.value) return;
    
    const freeOutputs = getFreeOutputsForClosure(stepId, output);
    
    selectionState.value = {
      mode: 'closure-select',
      closureData: {
        selectedOutputs: [{ stepId, output }],
      },
      availableTargets: freeOutputs,
    };
  };

  /**
   * Toggle an output selection for closure
   */
  const toggleClosureOutput = (stepId: string, output: number): void => {
    if (selectionState.value.mode !== 'closure-select' || !selectionState.value.closureData) return;
    
    const outputs = selectionState.value.closureData.selectedOutputs;
    const existingIndex = outputs.findIndex(
      o => o.stepId === stepId && o.output === output
    );
    
    if (existingIndex >= 0) {
      // Don't allow removing if it's the last one
      if (outputs.length > 1) {
        outputs.splice(existingIndex, 1);
      }
    } else {
      outputs.push({ stepId, output });
    }
  };

  /**
   * Check if an output is selected for closure
   */
  const isOutputSelectedForClosure = (stepId: string, output: number): boolean => {
    if (selectionState.value.mode !== 'closure-select' || !selectionState.value.closureData) return false;
    
    return selectionState.value.closureData.selectedOutputs.some(
      o => o.stepId === stepId && o.output === output
    );
  };

  /**
   * Get closure data for completion
   */
  const getClosureData = (): BranchClosureData | null => {
    if (selectionState.value.mode !== 'closure-select') return null;
    return selectionState.value.closureData || null;
  };

  /**
   * Check if closure can be completed (at least 2 outputs selected)
   */
  const canCompleteClosure = computed((): boolean => {
    if (selectionState.value.mode !== 'closure-select' || !selectionState.value.closureData) return false;
    return selectionState.value.closureData.selectedOutputs.length >= 2;
  });

  /**
   * Reset selection state to normal mode
   */
  const resetSelection = (): void => {
    selectionState.value = {
      mode: 'normal',
      availableTargets: [],
    };
  };

  /**
   * Cancel current operation
   */
  const cancel = (): void => {
    resetSelection();
  };

  return {
    // State (readonly)
    selectionState: readonly(selectionState),
    
    // Computed
    canShowBranchOptions,
    canCompleteClosure,
    
    // Merge operations
    startMerge,
    selectMergeTarget,
    getMergeData,
    
    // Closure operations
    startClosure,
    toggleClosureOutput,
    isOutputSelectedForClosure,
    getClosureData,
    
    // Common
    resetSelection,
    cancel,
    
    // Helpers
    getAncestors,
    getDescendants,
  };
}

export type BranchOperations = ReturnType<typeof useBranchOperations>;
